import type { CustomRouteContext } from 'core/universalRouter/types';
import { REQUEST_FROM } from 'store/reviewList/types';
import { fetchSEOData } from 'store/seo/action';

import { DEFAULT_SORT_OPTION } from 'constants/providerReview';
import { fetchMediaGallery } from 'store/mediaGallery/actions';
import { addToFavoriteList, fetchHeaderInfo } from 'store/providerCommonHeader/actions';
import { setIsJRDP, setJrdpQuery } from 'store/providerCommonHeader/slice';
import { fetchProviderFilters } from 'store/providerFilters/actions';
import { fetchProviderSummary } from 'store/providerSummary/actions';
import { fetchReviewList, fetchSelectedReviewList } from 'store/reviewList/actions';
import { PROVIDER_REVIEW_PAGE } from 'utils/ratHelper';
import { getDefaultURLSearchRequest } from 'utils/reviewHelper';

import { REVIEW_LIST_LOAD_MORE_LIMIT } from 'constants/common';
import { MEDIA_GALLERY_LIMIT } from 'constants/media';

import type { RouteContext } from 'universal-router';
import type { AppDispatch, Store } from 'react-redux';
import type { ReviewRequestBody } from 'store/reviewList/types';
import type { URLSearchQuery } from 'core/types/ReviewList';
import { fetchMediaConfig } from 'store/media/actions';
export type URLParams = {
  providerId: string;
  reviewId?: string;
};

function getCurrentUrl(context?: RouteContext<CustomRouteContext>): string {
  if (typeof window !== 'undefined') {
    return window.location.href;
  } else if (context?.req) {
    const req = context.req;

    return req.protocol + '://' + req.get('host') + req.originalUrl;
  }

  return '';
}

function redirectTo(url: string, context?: any) {
  if (typeof window !== 'undefined') {
    window.location.href = url;
  } else if (context?.res?.redirect) {
    context.res.redirect(url);
  }
}

export default async function providerResolver(
  context: RouteContext<CustomRouteContext>,
  store: Store,
): Promise<void> {
  const params: URLParams = context?.params as URLParams;
  const urlQueryParams: URLSearchQuery = context?.query as URLSearchQuery;

  const providerId = params.providerId;
  const dispatch: AppDispatch = store.dispatch;

  const requestParams = getDefaultURLSearchRequest(urlQueryParams);

  const query: ReviewRequestBody = {
    ...requestParams,
    keywords: Array.from(new Set(requestParams.keywords)),
    providerId: providerId,
    requestFrom: REQUEST_FROM.PROVIDER_REVIEW,
    limit: REVIEW_LIST_LOAD_MORE_LIMIT,
  };

  const reviewId = urlQueryParams?.reviewId;

  try {
    const { data } = await dispatch(fetchHeaderInfo(Number(providerId))).unwrap();

    if (!data.hotel.isDomestic) {
      const currentUrl = getCurrentUrl(context);
      const urlObj = new URL(currentUrl);

      urlObj.searchParams.set('f_overseas', 'true');
      redirectTo(urlObj.toString(), context);

      return;
    }
  } catch {
    // TODO: Remove after API fix
    // Temporary Catch to redirect all header API to old review page
    const currentUrl = getCurrentUrl(context);
    const urlObj = new URL(currentUrl);

    urlObj.searchParams.set('f_overseas', 'true');
    redirectTo(urlObj.toString(), context);

    return;
  }

  dispatch(setIsJRDP(Boolean(Number(urlQueryParams?.f_jrdp))));
  dispatch(setJrdpQuery(urlQueryParams?.f_jrdpquery));

  // Fetch outside of Promise.all to ensure it settles and does not fail page on reject
  await dispatch(fetchMediaConfig());

  await Promise.all([
    dispatch(fetchReviewList(query)),
    dispatch(fetchProviderSummary(Number(providerId))),
    dispatch(fetchProviderFilters(providerId)),
    dispatch(
      fetchMediaGallery({
        providerId: Number(providerId),
        offset: 0,
        limit: MEDIA_GALLERY_LIMIT,
      }),
    ),
  ]);

  if (store.getState().authCode.item.token && urlQueryParams.favoriteEntityId) {
    await dispatch(addToFavoriteList());
  }

  if (reviewId && query.sortBy === DEFAULT_SORT_OPTION) {
    await dispatch(fetchSelectedReviewList({ ...query, reviewId: reviewId }));
  }

  await dispatch(fetchSEOData({ pageName: PROVIDER_REVIEW_PAGE, providerId: params.providerId }));
}
